import React, { useEffect, useMemo, useRef, useState } from 'react';
import Button from '~/src/components/Button';
import PictureVideo from '~/src/components/PictureVideo';
import { imgBannerNavArrowSvg, imgBannerPauseSvg, imgBannerPlaySvg } from '~/src/images';
import { Urls } from '~/src/lib/enums/urls';
import { Banner } from '~/src/models/Banner';
import useFetch from '~/src/overtime-lib/src/www/hooks/useFetch';
import './BannerCarousel.scss';

const desktopWidthBreakpoint = 1024;

type BannerImagesProps = {
	banners: Banner[];
	currentIndex: number;
	previousIndex: number;
};

const BannerImages = ({ banners, currentIndex, previousIndex }: BannerImagesProps) => {
	return banners.map((banner, i) => {
		const isActive = i === currentIndex;
		const isPrevious = i === previousIndex && previousIndex !== currentIndex;
		return (
			<div
				key={`banner-image-${banner.id}`}
				className={`ImageContainer${isActive ? ' Active' : ''}${isPrevious ? ' Previous' : ''}`}
			>
				<PictureVideo alt={banner.title} src={banner.image_path} width={1980} mobileWidth={800} />
			</div>
		);
	});
};

type BannerContentProps = {
	banners: Banner[];
	isSingleBanner: boolean;
	isMobile: boolean;
	currentIndex: number;
	previousIndex: number;
};

const BannerContent = ({ banners, isSingleBanner, isMobile, currentIndex, previousIndex }: BannerContentProps) => {
	const contentPaddingStyle = isSingleBanner && isMobile ? ' SingleBannerMobileSpacing' : '';
	return (
		<>
			{/* Spacer: invisible version of the current content so our absolutely positioned animated content can be viewed */}
			<div className="ContentSpacer">
				<div className={`ContentContainer Active NoPositioning${contentPaddingStyle}`}>
					<div className="TextContainer">
						<h2 className="BannerTitle">{banners[currentIndex].title}</h2>
						<p className="BannerText">{banners[currentIndex].text}</p>
					</div>
					{banners[currentIndex].url_text && banners[currentIndex].url && (
						<div className="CtaContainer">
							<Button href={banners[currentIndex].url}>{banners[currentIndex].url_text}</Button>
						</div>
					)}
				</div>
			</div>

			{/* Animated banner content */}
			{banners.map((banner, i) => {
				const isActive = i === currentIndex;
				const isPrevious = i === previousIndex && previousIndex !== currentIndex;

				return (
					<div
						key={`banner-content-${banner.id}`}
						aria-hidden={!isActive}
						className={`ContentContainer${isActive ? ' Active' : ''}${isPrevious ? ' Previous' : ''}${contentPaddingStyle}`}
					>
						<div className="TextContainer">
							<h2 className="BannerTitle">{banner.title}</h2>
							<p className="BannerText">{banner.text}</p>
						</div>
						{banner.url_text && banner.url && (
							<div className="CtaContainer">
								<Button href={banner.url}>{banner.url_text}</Button>
							</div>
						)}
					</div>
				);
			})}
		</>
	);
};

type NavButtonProps = {
	direction: 'left' | 'right';
	className?: string;
	onClick?: () => void;
};

const NavButton = ({ onClick, className, direction }: NavButtonProps) => {
	return (
		<button
			className={`NavButton${direction === 'left' ? ' LeftNavButton' : ' RightNavButton'}${className ? `${className}` : ''}`}
			onClick={onClick}
			aria-label={`Navigate ${direction}`}
		>
			<img src={imgBannerNavArrowSvg} alt={direction === 'left' ? 'Previous banner' : 'Next banner'} />
		</button>
	);
};

type ProgressBarProps = {
	banners: Banner[];
	isPlaying: boolean;
	handlePausePlay: () => void;
	fillPercent: number[];
};

const ProgressBar = ({ banners, isPlaying, handlePausePlay, fillPercent }: ProgressBarProps) => {
	return (
		<div className="ProgressBar">
			<button className="PlayPauseButton" onClick={handlePausePlay} aria-label="Play/Pause slideshow">
				<img
					src={isPlaying ? imgBannerPauseSvg : imgBannerPlaySvg}
					alt={isPlaying ? 'Pause banner slideshow' : 'Play banner slideshow'}
				/>
			</button>
			<div className="SegmentWrapper">
				{banners.map((_, i) => (
					<div className="ProgressSegment" key={`progress-segment-${i}`}>
						<div className="ProgressFill" style={{ width: `${fillPercent[i]}%` }}></div>
					</div>
				))}
			</div>
		</div>
	);
};

const BannerCarousel = ({ slideDuration, site }: { slideDuration: number; site: string }) => {
	const banners = useFetch<Banner[]>({
		url: `${Urls.ApiRestBannersUrl}?site=${site}`,
		key: 'banners',
	}).result.read();

	if (!banners || banners.length === 0) {
		return null;
	}

	// STATE
	const [currentIndex, setCurrentIndex] = useState(0);
	const [previousIndex, setPreviousIndex] = useState<number>(-1);
	const [isPlaying, setIsPlaying] = useState(true);
	// keeps track of how long the current slide has been showing
	const [elapsed, setElapsed] = useState(0);
	const [isMobile, setIsMobile] = useState(() =>
		typeof window !== 'undefined' ? window.innerWidth < desktopWidthBreakpoint : false,
	);

	// REFS
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);
	const slideStartTimeRef = useRef(Date.now());
	const justAdvancedRef = useRef(false);
	const containerRef = useRef<HTMLDivElement>(null);

	const isSingleBanner = useMemo(() => banners.length === 1, [banners]);

	// Calculate window width on resize to properly render progress bar
	useEffect(() => {
		let resizeTimeout: NodeJS.Timeout;
		const handleResize = () => {
			clearTimeout(resizeTimeout);
			resizeTimeout = setTimeout(() => {
				setIsMobile(window.innerWidth <= 1023);
			}, 150);
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	// Sets duration of each slide after auto advancing or pause/playing
	useEffect(() => {
		if (!isPlaying || banners.length === 0 || isSingleBanner) return;

		let timeLeft: number;
		if (justAdvancedRef.current) {
			setElapsed(0);
			slideStartTimeRef.current = Date.now();
			timeLeft = slideDuration;
			justAdvancedRef.current = false;
		} else {
			slideStartTimeRef.current = Date.now() - elapsed;
			timeLeft = slideDuration - elapsed;
		}

		timeoutRef.current = setTimeout(() => {
			setCurrentIndex((prev) => {
				const next = (prev + 1) % banners.length;
				setPreviousIndex(prev);
				return next;
			});
			justAdvancedRef.current = true;
		}, timeLeft);

		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, [currentIndex, banners.length, isPlaying]);

	// Sets the elapsed time for the progress bar
	useEffect(() => {
		if (!isPlaying || isSingleBanner) return;

		const interval = setInterval(() => {
			setElapsed(Date.now() - slideStartTimeRef.current);
		}, 100);

		return () => clearInterval(interval);
	}, [isPlaying]);

	const fillPercent = useMemo(() => {
		return banners.map((_, i) => {
			const isCurrent = i === currentIndex;
			const isCompleted = i < currentIndex;

			return isMobile ? (isCurrent ? 100 : 0) : isCurrent ? (elapsed / slideDuration) * 100 : isCompleted ? 100 : 0;
		});
	}, [currentIndex, elapsed, isMobile]);

	const updateSlide = (nextIndex: number) => {
		setPreviousIndex(currentIndex);
		setCurrentIndex(nextIndex);
		setElapsed(0);
		justAdvancedRef.current = true;
	};

	const handleNext = () => {
		const nextIndex = (currentIndex + 1) % banners.length;
		updateSlide(nextIndex);
	};

	const handlePrev = () => {
		const nextIndex = (currentIndex - 1 + banners.length) % banners.length;
		updateSlide(nextIndex);
	};

	const handlePausePlay = () => {
		if (isPlaying) {
			setElapsed(Date.now() - slideStartTimeRef.current);
			setIsPlaying(false);
		} else {
			setIsPlaying(true);
		}
	};

	return (
		<div className="BannerCarousel">
			<div className="ImagesContentContainer">
				<div className="BannerImagesContainer" ref={containerRef}>
					<BannerImages banners={banners} currentIndex={currentIndex} previousIndex={previousIndex} />
					{!isSingleBanner && (
						<>
							<NavButton direction="left" onClick={handlePrev} />
							<NavButton direction="right" onClick={handleNext} />
						</>
					)}
				</div>
				<div className="BannerContentContainer">
					<BannerContent
						banners={banners}
						isSingleBanner={isSingleBanner}
						isMobile={isMobile}
						currentIndex={currentIndex}
						previousIndex={previousIndex}
					/>
				</div>
			</div>

			{!isSingleBanner && (
				<ProgressBar
					banners={banners}
					isPlaying={isPlaying}
					handlePausePlay={handlePausePlay}
					fillPercent={fillPercent}
				/>
			)}
		</div>
	);
};

export default BannerCarousel;
